import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getPrintersByBranch, updatePrinter, changePrinterStatus } from "../../../../../services/Settings/printers";
import { fetchActiveBranches } from "../../../../../services/dashboard/apiServiceDashboard";
import AddPrinterForm from "./AddPrinterForm"; // ✅ تأكد من المسار
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const PrintersListByBranch = () => {
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPrinter, setEditPrinter] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    cashier_printer: 0,
    local_url: "",
    root_id: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    const loadBranchesAndDefault = async () => {
      try {
        const branchesData = await fetchActiveBranches();
        setBranches(branchesData);

        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const savedRootId = user.root_id;

        if (savedRootId) {
          setSelectedBranchId(savedRootId);
          loadPrinters(savedRootId);
        }
      } catch (err) {
        setError("❌ فشل في تحميل الفروع");
      }
    };

    loadBranchesAndDefault();
  }, []);

  const loadPrinters = async (branchId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPrintersByBranch(branchId);
      setPrinters(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = (e) => {
    const branchId = parseInt(e.target.value);
    setSelectedBranchId(branchId);
    if (!isNaN(branchId)) {
      loadPrinters(branchId);
    }
  };

  const handlePrinterAdded = () => {
    if (selectedBranchId) {
      loadPrinters(selectedBranchId);
      setOpenDialog(false);
    }
  };

  // Add handlers for edit and delete (implement logic as needed)
  const handleEditPrinter = (printer) => {
    setEditPrinter(printer);
    setEditForm({
      name: printer.name || "",
      cashier_printer: printer.cashier_printer || 0,
      local_url: printer.local_url || "",
      root_id: printer.root_id || "",
    });
    setEditDialogOpen(true);
  };

  const handleDeletePrinter = async (printer) => {
    if (!window.confirm(`هل تريد تعطيل الطابعة "${printer.name}"؟`)) return;
    try {
      await changePrinterStatus(printer.id);
      alert(`تم تعطيل الطابعة: ${printer.name}`);
      loadPrinters(selectedBranchId);
    } catch (err) {
      alert(err.message || "فشل في تعطيل الطابعة.");
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: name === "cashier_printer" ? Number(value) : value,
    }));
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    setEditError("");
    try {
      await updatePrinter(editPrinter.id, editForm);
      setEditDialogOpen(false);
      setEditPrinter(null);
      loadPrinters(selectedBranchId);
    } catch (err) {
      setEditError(err.message || "❌ فشل في تحديث بيانات الطابعة.");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        🖨️ قائمة الطابعات حسب الفرع
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>اختر الفرع</InputLabel>
        <Select
          value={selectedBranchId}
          onChange={handleBranchChange}
          label="اختر الفرع"
        >
          {branches.map((branch) => (
            <MenuItem key={branch.id} value={branch.id}>
              {branch.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && <CircularProgress />} 
      {error && <Alert severity="error">{error}</Alert>}

      {selectedBranchId && !loading && !error && (
        <>
          <Paper variant="outlined" sx={{ mt: 2 }}>
            <List>
              {printers.filter((printer) => printer.status !== 0).length === 0 ? (
                <ListItem>
                  <ListItemText primary="لا توجد طابعات لهذا الفرع" />
                </ListItem>
              ) : (
                printers
                  .filter((printer) => printer.status !== 0) // ✅ إخفاء الطابعات المعطلة
                  .map((printer) => (
                    <ListItem key={printer.id} divider
                      secondaryAction={
                        <Box>
                          <IconButton color="primary" onClick={() => handleEditPrinter(printer)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleDeletePrinter(printer)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={printer.name}
                        secondary={
                          printer.cashier_printer === 1
                            ? "✅ طابعة كاشير"
                            : "🖨️ طابعة عادية"
                        }
                      />
                    </ListItem>
                  ))
              )}
            </List>
          </Paper>

          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            onClick={() => setOpenDialog(true)}
          >
            ➕ إضافة طابعة جديدة
          </Button>

          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              إضافة طابعة جديدة
              <IconButton onClick={() => setOpenDialog(false)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <AddPrinterForm
                onSuccess={handlePrinterAdded}
                defaultRootId={selectedBranchId}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>تعديل بيانات الطابعة</DialogTitle>
            <DialogContent>
              {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
              <TextField
                label="اسم الطابعة"
                name="name"
                value={editForm.name}
                onChange={handleEditFormChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="رابط الطابعة المحلي"
                name="local_url"
                value={editForm.local_url}
                onChange={handleEditFormChange}
                fullWidth
                margin="normal"
              />
              <TextField
                select
                label="نوع الطابعة"
                name="cashier_printer"
                value={editForm.cashier_printer}
                onChange={handleEditFormChange}
                fullWidth
                margin="normal"
              >
                <MenuItem value={1}>طابعة كاشير</MenuItem>
                <MenuItem value={0}>طابعة عادية</MenuItem>
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)}>إلغاء</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEditSubmit}
                disabled={editLoading}
              >
                {editLoading ? "جارٍ الحفظ..." : "حفظ التعديلات"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default PrintersListByBranch;

